import { IColumnDefinition, ReportType } from '@common/types';
import { FC, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  TableContainer,
  TableSortLabel,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { theme } from '@store';
import { formatDate } from '@util/date.ts';
import { Https, SearchOff, Star } from '@mui/icons-material';
import { t } from 'i18next';
import { Dropdown, DropdownMenuItem, ReportHistoryModal } from '@components';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { reportTypeLabelMapper } from '@util/reportTypeLabelMapper.ts';
import { Link } from 'react-router-dom';
import { ReportsFolderPreviewFilters } from '@views/pages/Reporting/ReportsFolderPreview/types.ts';
import {
  useReportsFolderPreviewTable,
  ColumnNames,
  ReportsFolderPreviewTableEntry,
} from '@views/pages/Reporting/ReportsFolderPreview/hooks';

interface ReportingTableProps {
  rows: ReportsFolderPreviewTableEntry[];
  columns: IColumnDefinition[];
  filters: ReportsFolderPreviewFilters;
  setFilters: (filters: ReportsFolderPreviewFilters) => void;
  totalCount: number;
  loading: boolean;
  reportIdToFavorite: string;
  makeReportFavoriteIsPending: boolean;
  handleOpenDeleteModal: (id: string) => void;
  handleMakeReportFavorite: (id: string, isFavorite: boolean) => void;
}

export const ReportsFolderPreviewTable: FC<ReportingTableProps> = ({
  rows,
  columns,
  filters,
  setFilters,
  totalCount,
  loading,
  makeReportFavoriteIsPending,
  handleOpenDeleteModal,
  handleMakeReportFavorite,
  reportIdToFavorite,
}) => {
  const {
    sort_order,
    sort_by,
    onPageSizeChange,
    handleChangeOrder,
    onPageIndexChange,
    handleEditReport,
    handleGenerateReportCSV,
    isDownloading,
    user_id,
    isReportHistoryModalOpen,
    handleCloseHistoryModal,
    historyReportId,
    handleOpenHistoryModal,
  } = useReportsFolderPreviewTable({
    filters,
    setFilters,
  });

  const stableCountRef = useRef(totalCount);
  const stableCount = useMemo(() => {
    if (!loading) {
      stableCountRef.current = totalCount;
    }
    return stableCountRef.current;
  }, [loading, totalCount]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: `calc(100vh - 400px)`,
          borderRadius: 0,
          boxShadow: 'none',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          borderTop: 'none',
        }}
      >
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  paddingY: '1rem',
                  bgcolor: theme.palette.headerBackground.main,
                },
              }}
            >
              {columns.map((col, index) => (
                <TableCell key={`${col.id}-${index}`} align='left'>
                  {col.sortable ? (
                    <TableSortLabel
                      active={true}
                      direction={
                        col.id === sort_by
                          ? (sort_order?.toLowerCase() as 'asc' | 'desc')
                          : 'asc'
                      }
                      onClick={() => handleChangeOrder(col.id as ColumnNames)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {rows.length > 0 &&
                rows.map(row => {
                  const dropdownMenu: DropdownMenuItem[] = [
                    {
                      iconName: 'none',
                      label: t('reporting.report.table.edit'),
                      onClick: () => handleEditReport(row.id),
                      disabled: user_id !== row.created_by?.id,
                    },
                    {
                      iconName: 'none',
                      label: t('reporting.report.table.export'),
                      onClick: () => handleGenerateReportCSV(row),
                      disabled: isDownloading,
                    },
                    {
                      iconName: 'none',
                      label: t('reporting.report.table.viewHistory'),
                      onClick: () => handleOpenHistoryModal(row.id),
                    },
                    {
                      iconName: 'none',
                      label: t('reporting.report.table.delete'),
                      onClick: () => handleOpenDeleteModal(row.id),
                      disabled: user_id !== row.created_by?.id,
                      borderTop: true,
                    },
                  ];

                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        cursor: 'pointer',
                        '& .MuiTableCell-body': {
                          paddingY: '1rem',
                        },
                        '&:hover, &:nth-of-type(even):hover': {
                          backgroundColor: theme.palette.hoverTableRowBg.main,
                        },
                      }}
                      component={Link}
                      to={`/reporting/items/${row.id}`}
                    >
                      <TableCell align='right'>
                        <IconButton
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleMakeReportFavorite(row.id, row.favorite);
                          }}
                          loading={
                            makeReportFavoriteIsPending &&
                            reportIdToFavorite === row.id
                          }
                          sx={{ height: '24px', width: '24px' }}
                        >
                          <Star
                            color={row.favorite ? 'primary' : 'disabled'}
                            sx={{
                              display:
                                makeReportFavoriteIsPending &&
                                reportIdToFavorite === row.id
                                  ? 'none'
                                  : 'block',
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell
                        sortDirection='desc'
                        component='th'
                        scope='row'
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            maxWidth: '15vw',
                          }}
                        >
                          <TruncatedTextWithConditionalTooltip
                            width='100%'
                            text={row.name}
                          />
                          <TruncatedTextWithConditionalTooltip
                            width='100%'
                            sx={{
                              color: theme => theme.palette.semiBlack.main,
                            }}
                            variant='body2'
                            text={reportTypeLabelMapper(
                              row.report_type as ReportType,
                            )}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align='left'>
                        <TruncatedTextWithConditionalTooltip
                          text={row.description}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <TruncatedTextWithConditionalTooltip
                          text={row.folder.name}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <TruncatedTextWithConditionalTooltip
                          text={row.folder.organization?.name}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <TruncatedTextWithConditionalTooltip
                            text={row.created_by?.name}
                          />
                          <TruncatedTextWithConditionalTooltip
                            color={theme.palette.semiBlack.main}
                            variant='body2'
                            text={formatDate(row.created_at)}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align='left'>
                        {row.private ? (
                          <Https color='disabled' sx={{ height: '24px' }} />
                        ) : (
                          ''
                        )}
                      </TableCell>
                      <TableCell align='right'>
                        <Dropdown menuList={dropdownMenu} actionMenu />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </>
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length === 0 && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: 'calc(100vh - 500px)',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          {loading ? (
            <CircularProgress size='64px' />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <SearchOff
                sx={{ width: 46, height: 46, mb: 3 }}
                color='primary'
              />
              <Typography variant='h6'>
                {t('patient.documents.table.noResults.title')}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                {t('patient.documents.table.noResults.subTitle')}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <TablePagination
        sx={{
          borderBottom: 0,
          paddingY: '0.75rem',
          zIndex: 100,
          bgcolor: 'white',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          left: 0,
        }}
        component='div'
        count={stableCount}
        page={filters.page! - 1}
        rowsPerPage={filters.limit!}
        onPageChange={onPageIndexChange}
        onRowsPerPageChange={onPageSizeChange}
      />
      <ReportHistoryModal
        isOpen={isReportHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        report_id={historyReportId}
      />
    </Box>
  );
};
